<template>
	<loading-el	v-if="loading"></loading-el>
	<div class="popup-header">
		<h2 class="popup-headline ellipsis">{{ files.name }}</h2>
		<div
			@click="closeUpload"
			class="popup-header__back white-space--no-wrap">
			<i class="fas fa-chevron-left"></i>
			<span>{{ t('popup.media.button.back') }}</span>
		</div>
	</div>
  <alert-message
      v-if="alert.isShown"
      :alert="alert"
      @close-alert="alertHide"
      class="popup-alert"
  ></alert-message>
	<div class="media-details">
		<div class="media-details__file">
			<figure v-if="isSingleFileUploading" class="media-figure">
				<img v-if="files[0].type.includes('image') && !loading" :src="image" alt="">
				<video v-else-if="files[0].type.includes('video')" controls :src="video"></video>
				<canvas v-else-if="lottieDataUrl" ref="lottieCanvas" style="width:100%;height:200px"></canvas>
				<i v-else class="fas fa-file-alt media-figure__icon"></i>
			</figure>
			<media-list
				v-else
				:files="fileList"
			></media-list>
			<div class="button-wrapper media-details__button-wrapper mb--30 mb--42:desktop">
				<div
					@click="fileUpload"
					:class="isUploadAvailableClassname"
					class="button button--popup bg--black media-details__button"
				>
					<i class="fas fa-upload"></i>
					<span v-if="isSingleFileUploading">{{ t('popup.media.button.uploadFile') }}</span>
					<span v-else>{{ t('popup.mediaList.uploadButton') }}</span>
				</div>
			</div>
			<div class="popup-label mb--20">{{ t('popup.media.label.templates') }}</div>
			<file-meta
				v-model:target-containers="containers"
				v-model:content-types="types"
			></file-meta>
		</div>
		<media-info
			:files="files"
		></media-info>
	</div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import ApiService from '@/utils/ApiService';
import FileMeta from '@/components/editor-options/settings/popup/content/media/FileMeta';
import LoadingEl from '@/components/ui/loading/LoadingEl.vue';
import useLoading from '@/utils/Loading';
import {ref, computed, nextTick, onBeforeUnmount} from 'vue';
import {useStore} from 'vuex';
import useAlert from '@/utils/Alert';
import AlertMessage from '@/components/ui/alert/AlertMessage.vue';
import {ALERT_TYPES} from '@/models/ALERT_TYPES';
import MediaInfo from "@/components/editor-options/settings/popup/content/media/MediaInfo";
import MediaList from "@/components/editor-options/settings/popup/content/media/MediaList";
import {DotLottie} from '@lottiefiles/dotlottie-web';

export default {
	components: {
		MediaList,
		MediaInfo,
		FileMeta,
		LoadingEl,
		AlertMessage,
	},
	props: {
		files: {
			required: true
		}
	},
	emits: ["insertMedia", "closeUpload", "mediaError"],
	setup(props, {emit}) {
		const {t} = useI18n();
		// loading
		const { loading, loadingShow, loadingHide } = useLoading();
		// alert
		const { alert, alertHide, alertShow, alertCreate } = useAlert();
		
		const store = useStore();
		const currentMap = computed(() => store.getters['maps/getCurrentMap'])
		const assetsURL = computed(() => store.getters['mediaAssets/getAssetsURL']);
		
		const containers = ref([]);
		const types = ref([]);
		
		const image = ref(null);
		const video = ref(null);
		const lottieDataUrl = ref(null);
		const lottieCanvas = ref(null);
		let lottieInstance = null;

		async function convertFile(file){
			loadingShow();
			if(isLottieFile(file)){
				if (/\.json$/i.test(file.name) && !(await validateLottieJson(file))) {
					alertCreate({type: ALERT_TYPES.ERROR, message: t('popup.media.message.invalidLottie')});
					alertShow();
					loadingHide();
					return;
				}
				const blobUrl = URL.createObjectURL(file);
				lottieDataUrl.value = blobUrl;
				nextTick(() => {
					if(lottieCanvas.value){
						lottieInstance = new DotLottie({
							canvas: lottieCanvas.value,
							src: blobUrl,
							autoplay: true,
							loop: true
						});
					}
				});
				loadingHide();
				return;
			}
			const reader = new FileReader();
			reader.onload = () => {
				if(file.type.includes('image')) image.value = reader.result;
				if(file.type.includes('video')) video.value = reader.result;
				loadingHide();
			}
			reader.readAsDataURL(file);
		}

		onBeforeUnmount(() => {
			if(lottieInstance){
				lottieInstance.destroy();
				lottieInstance = null;
			}
		});
		
		// file list
		const fileList = ref([]);
		
		async function fileListConvert(files){
			const result = [];
			for (const file of files) {
				const accepted = await meetsRequirements(file);
				const message = accepted ? t('popup.mediaList.message.accepted') : t('popup.mediaList.message.denied')
				result.push({name: file.name,	accepted,	message});
			}
			return result;
		}

		async function fileListCheckRequirements(){
			// init fileList
			fileList.value = await fileListConvert(props.files);
			// if any of file meets requirements it can be uploaded
			return fileList.value.some(file => file.accepted);
		}
		
		function isLottieFile(file){
			return /\.(lot|lottie|lottie\.json|json)$/i.test(file.name);
		}

		function validateLottieJson(file) {
			return new Promise((resolve) => {
				const reader = new FileReader();
				reader.onload = () => {
					try {
						const data = JSON.parse(reader.result);
						const hasRequiredKeys = ['v', 'layers', 'op', 'ip', 'fr', 'w', 'h']
							.every(key => key in data);
						resolve(hasRequiredKeys);
					} catch {
						resolve(false);
					}
				};
				reader.onerror = () => resolve(false);
				reader.readAsText(file);
			});
		}

		async function meetsRequirements(file){
			if (/\.json$/i.test(file.name)) {
				const isLottie = await validateLottieJson(file);
				return isLottie && file.size / (1024 * 1024) < 5;
			}
			return !!(isLottieFile(file) && file.size / (1024 * 1024) < 5 ||
				file.type.includes('image/svg') ||
				file.type.includes('image') && file.size / (1024 * 1024) < 5 ||
				file.type.includes('application') && file.size / (1024 * 1024) < 500 ||
        file.type.includes('text') && file.size / (1024 * 1024) < 500 ||
				file.type.includes('audio') && file.size / (1024 * 1024) < 10 ||
				file.type.includes('video') && file.size / (1024 * 1024) < 500);
		}
		
		const isSingleFileUploading = computed(() => props.files.length === 1);
		const isUploadAvailable = ref(false);
		const isUploadAvailableClassname = computed(() => isUploadAvailable.value ? "" : "disabled");

		async function checkUploadAvailable() {
			isUploadAvailable.value = isSingleFileUploading.value
				? await meetsRequirements(props.files[0])
				: await fileListCheckRequirements();
		}
		checkUploadAvailable();
		
		async function fileUpload(){
      alertHide();
			
			const formData = new FormData();
			const data = {content_types: [...types.value], target_containers: [...containers.value]};
			
			for(let i = 0; i < props.files.length; i++){
				// add file to formData only if meet requirements
				if(await meetsRequirements(props.files[i]))	{
          formData.append(`file`, props.files[i])
        } else {
         return;
        }
			}
      loadingShow();
			formData.append('target', currentMap.value._links.self);
			formData.append('data', JSON.stringify(data));
			
			try{
				const {data: {results: media}} = await ApiService.post(`${assetsURL.value}`,
					formData,
					{ headers: { "Content-Type": "multipart/form-data" } })
				
				emit('insertMedia', media);
				closeUpload();
			} catch(error){
				loadingHide();
				alertCreate({type: ALERT_TYPES.ERROR, message: error.response?.data?.detail || t('popup.media.message.errorUpload')});
        alertShow();
			}
		}
		
		function closeUpload(){
			emit('closeUpload');
		}
		
		// PROCESS
		if(isSingleFileUploading.value) convertFile(props.files[0]);
		
		return{
			t,
			alert,
			alertHide,
			loading,
			isUploadAvailableClassname,
			closeUpload,
			fileUpload,
			containers,
			types,
			image,
			video,
			lottieDataUrl,
			lottieCanvas,
			isSingleFileUploading,
			fileList,
		}
	}
}
</script>

<style lang="scss">

</style>
